import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { CheckoutPaymentMethodTypes } from 'src/app/+checkout-purchase/components/checkout-payment-methods.component/enums';
import { StorageKeys } from 'src/app/model/enums/storage-keys';
import { Fee } from 'src/app/model/interfaces/fee';
import { LotteryClass } from 'src/app/model/interfaces/lottery-class';
import { MyTicketResponse } from 'src/app/model/interfaces/my-ticket-response';
import { ReceiptOrders } from 'src/app/model/interfaces/receipt-orders';
import { Share } from 'src/app/model/interfaces/share';
import { SimplePlayer } from 'src/app/model/interfaces/simple-player';
import { TicketPackageInstance } from 'src/app/model/interfaces/ticket-package-instance';
import { ShareType } from 'src/app/model/share-type';
import { Visitor } from 'src/app/model/visitor';
import { OrderService, PlayerService, ResourceService, StorageService, TicketService, VisitorService } from 'src/app/services';
import { AuthorizationService } from 'src/app/services/authorization/authorization.service';
import { PriceService } from 'src/app/services/price.service';
import { ReceiptService } from 'src/app/services/receipt-service';
import { SubscriptionService } from 'src/app/services/subscription.service';
import { TrackingFacadeService } from 'src/app/services/tracking/tracking-facade.service';
import { MembershipState } from '../../model/enums/membership-state';
import { PaymentMethodType } from '../../model/enums/payment-method-type';
import { Order } from '../../model/interfaces/order';
import { LotteryService } from '../../services/lottery.service';

declare interface eventBusInteface {
    dispatchEvent(event: CustomEvent): void;
}
declare var myEventBus: eventBusInteface;

@Component({
    selector: 'app-receipt',
    templateUrl: 'receipt.component.html',
    styleUrls: ['receipt.component.scss']
})

export class ReceiptComponent implements OnInit, OnDestroy
{
    @ViewChild('receiptMiddle')
    public receiptMiddle: ElementRef;
    
    public orders: ReceiptOrders;
    public orderId: any;
    public visitor: Visitor = {};
    public player: SimplePlayer;
    public winChance: number;
    public circleChance: number;
    public finalized: boolean;
    public totalPrice: number;
    public orderedShareCount = 0;
    public ownedShareCount: number;
    public creditCardPaymentAmount: number;
    public mostValuableTicket: ShareType;
    public mostValuableTicketString: any;
    public mostValuableTicketStringSliced: any;
    public firstName: string;
    public isAuthorized = false;
    public accordionIsOpen = false;
    public accordionGapfundIsOpen = false;
    public dateOfPurchase = new Date();
    public fmtOrder: Order;
    public nextDrawDate: Date;
    public paymentPeriod: number;
    public gapfundDeclined = false;
    public showNextBSSaleStopDate = false;
    public subscriptionPaymentType: number;
    public smtOrderId: number;
    public smtOrder: Order;
    public BSPurchaseWithDeclinedGapfund = false;
    public BSFees: Fee[];
    public isBsWithoutGapfund = false;
    // Expose enum to template
    public CheckoutPaymentMethodTypes = CheckoutPaymentMethodTypes

    public get showBsGapfundReceipt(): boolean {
      return this.paymentMethodUsed === CheckoutPaymentMethodTypes.Betalingsservice && !this.isBsWithoutGapfund;
    }

    private readonly visitorService: VisitorService;
    private readonly resourceService: ResourceService;
    private readonly ticketService: TicketService;
    private readonly playerService = inject(PlayerService);
    private readonly receiptService: ReceiptService;
    private readonly subscriptionService: SubscriptionService;
    private readonly authorizationService: AuthorizationService;
    private readonly router: Router;
    private ownedShares: Share[] = [];
    private getPlayerNameSubscription: Subscription;
    private getPlayerSharesSubscription: Subscription;
    private readonly storageService: StorageService;
    private readonly priceService: PriceService;
    private readonly lotteryService: LotteryService;
    private readonly trackingFacadeService: TrackingFacadeService;
    private readonly orderService: OrderService;
    public bsSalesDate: Date;
    public isPurchasing = false;
    public order: Order;
    public paymentMethodUsed: number;
    private membershipState: MembershipState;

    public constructor(
        visitorService: VisitorService,
        resourceService: ResourceService,
        ticketService: TicketService,
        receiptService: ReceiptService,
        subscriptionService: SubscriptionService,
        authorizationService: AuthorizationService,
        router: Router,
        storageService: StorageService,
        lotteryService: LotteryService,
        priceService: PriceService,
        trackingFacadeService: TrackingFacadeService,
        orderService: OrderService
    ) {
        this.visitorService = visitorService;
        this.resourceService = resourceService;
        this.ticketService = ticketService;
        this.receiptService = receiptService;
        this.subscriptionService = subscriptionService;
        this.authorizationService = authorizationService;
        this.router = router;
        this.storageService = storageService;
        this.lotteryService = lotteryService;
        this.priceService = priceService;
        this.trackingFacadeService = trackingFacadeService;
        this.orderService = orderService;
    }

    public async ngOnInit(): Promise<void> {
      this.paymentMethodUsed = Number(sessionStorage.getItem(StorageKeys.PaymentMethodUsed));
      this.paymentPeriod = Number(sessionStorage.getItem(StorageKeys.PaymentPeriod));
      
      this.orders = this.receiptService.getOrders();

      // The receipt's checkout-flow step (flow_info step 3, 'receipt') is carried on the page_view
      // itself — see TrackingFacadeService.getPageViewFlow() — so no separate flow event is fired here.

      // Determine if this BS purchase cannot offer gap funding.
      // Gap funding requires FMTClassId > 0 (sale window is open) and
      // FMTClassId !== SubscriptionStartClassId (player isn't already in the earliest class).
      // FMTClassId is 0 around the 15th–20th of the month when Nets processes BS payments.
      // When unavailable, the receipt falls back to the standard (non-BS) design.
      // Computed before the first await to prevent template flicker.
      if (this.paymentMethodUsed === CheckoutPaymentMethodTypes.Betalingsservice) {
        const order = this.orders?.orderSubscription;
        if (order) {
          const canOfferGapFunding = order.FMTClassId > 0 && order.FMTClassId !== order.SubscriptionStartClassId;
          this.isBsWithoutGapfund = !canOfferGapFunding;
        }
      }

      this.membershipState = await this.playerService.membershipState(1);

      // Run standard receipt flow for non-BS payments, or BS purchases where gap funding is unavailable
      if(this.paymentMethodUsed !== CheckoutPaymentMethodTypes.Betalingsservice || this.isBsWithoutGapfund) {
        this.gapfundDeclined = this.storageService.getItem<boolean>(StorageKeys.GapfundDeclined) === true;

        // When gap funding is not available for a BS purchase, treat as declined
        if (this.isBsWithoutGapfund) {
          this.gapfundDeclined = true;
        }
        this.storageService.setItem(StorageKeys.BasketTicketsCount, 0);
        this.dispatchEvent();
       
        this.isAuthorized = await this.authorizationService.isAuthorized();
    
        if (this.isAuthorized) {
            this.getPlayerNameSubscription = this.getPlayerName().subscribe(name => this.firstName = name);
            this.getPlayerSharesSubscription = this.getSharesByPlayer().subscribe(shares => {

                if (!shares) {
                    return;
                }

                this.ownedShares = shares;
                this.buildModel();

            });
        } else {
            this.firstName = this.visitorService.getVisitor().FirstName;
            this.buildModel();
        }

        const currentOrder: Order = this.orders.orderSubscription === null && this.orders.orderSmt !== null ? this.orders.orderSmt : this.orders.orderSubscription;
        this.priceService.getPriceByOrder(currentOrder).subscribe((priceobj) => {
            this.totalPrice = priceobj.totalPrice;
            this.BSFees = priceobj.fees;
        });
        
        this.handlePaymentDetails();
        this.getNextDrawDate();
        this.getChosenPaymethodAndTrackEvent();
      }

      // Update ShopOrderId and TrackableOrderId from the receipt data
      // that is already available, avoiding unnecessary API calls and
      // preventing creation of a new empty order after a completed payment.
      const receiptOrder = this.orders?.orderSubscription ?? this.orders?.orderSmt;
      if (receiptOrder) {
        this.storageService.setItem(StorageKeys.ShopOrderId, receiptOrder.Id);
        this.storageService.setItem(StorageKeys.TrackableOrderId, receiptOrder.Id);
      }
    }

    private handlePaymentDetails(): void
    {
        this.subscriptionPaymentType = Number(sessionStorage.getItem(StorageKeys.PaymentMethodUsed));
        this.smtOrder = Object(sessionStorage.getItem(StorageKeys.GapFundingOrder));

        if(this.smtOrder)
        {
            this.lotteryService.getCurrentLottery().subscribe((lottery) => {
                this.smtOrder.FMTClassId = lottery.LotteryClasses[0].ClassIndex;
            });
        }

        // BS purchase with declined/unavailable gap funding: show next BS sale stop countdown
        if(this.subscriptionPaymentType === CheckoutPaymentMethodTypes.Betalingsservice && this.gapfundDeclined) {
            this.BSPurchaseWithDeclinedGapfund = true;
            this.showNextBSSaleStopDate = true;
        }
    }

  private getChosenPaymethodAndTrackEvent(): void {
    const order = this.orders.orderSubscription ?? this.orders.orderSmt;
    const chosenPaymentMethodForSubscription = this.storageService.getItem(StorageKeys.PaymentMethodUsed);
    const chosenPaymentMethodForGapfund = this.storageService.getItem(StorageKeys.PaymentMethodUsedGapfund);
    const gapfundPaymentMethod = chosenPaymentMethodForGapfund === CheckoutPaymentMethodTypes.BillwerkApplepay
      ? PaymentMethodType.ApplePay
      : chosenPaymentMethodForGapfund === CheckoutPaymentMethodTypes.MobilePay
        ? PaymentMethodType.MobilePay
        // Keep Vipps recurring explicit so analytics does not fall back to card.
        : chosenPaymentMethodForGapfund === CheckoutPaymentMethodTypes.VippsRecurring
          ? PaymentMethodType.VippsRecurring
        : PaymentMethodType.Card;

    if (chosenPaymentMethodForSubscription !== null) {
      if (chosenPaymentMethodForSubscription === CheckoutPaymentMethodTypes.Betalingsservice) {
        const bsTrackingOrder = this.orders.orderSmt ?? this.orders.orderSubscription;
        this.trackingFacadeService.trackOrderOnPaymentFlowPayMethodBS(bsTrackingOrder, this.membershipState);
        // Fire the purchase event for Betalingsservice too, so card and BS are symmetric (payment_method = "Betalingsservice").
        this.orderService.trackPurchaseComplete(bsTrackingOrder, this.membershipState, PaymentMethodType.Betalingsservice);
      } else {
        this.orderService.trackPurchaseComplete(order, this.membershipState, gapfundPaymentMethod);
        if (!this.gapfundDeclined) {
          this.orderService.trackGapfundComplete(order, this.membershipState, gapfundPaymentMethod);
        }
      }
    }
  }

    public getNextDrawDate(): void
    {
        const order = this.orders.orderSubscription ?? this.orders.orderSmt;
        let classId = this.BSPurchaseWithDeclinedGapfund ? order.SubscriptionStartClassId : order.FMTClassId;

        // if classId is null, use the value from session storage for SMT purchase
        // ADGR: Could we get LotteryClassId it from orderService.getOrderById(smtOrderId) and then kill the session storage usage?
        if(classId === 0 || classId === null) {
            const lotteryClassId: number = this.storageService.getItem(StorageKeys.LotteryClassId);
            classId = lotteryClassId;
        }

        this.lotteryService.getLotteryClassById(classId)
            .subscribe((lotteryClass: LotteryClass) => {
            this.nextDrawDate = new Date(lotteryClass.Draws[0].DrawDate);
        });
    }

  private getPlayerName(): Observable<string> {
    return this.playerService.getPlayerObservable().pipe(
      map(player => player?.FirstName ?? '')
    );
  }

  private getSharesByPlayer(): Observable<Share[]> {
    return this.getSharesByPlayerSubscriptions();
  }

    private getSharesByPlayerSubscriptions(): Observable<Share[]> {
        return this.subscriptionService.getMyTickets().pipe(map(subscriptions => {
            if (subscriptions == null) {
                return null;
            }

            return this.getFlattenedTickets(subscriptions);
        }));
    }

  private getFlattenedTickets(subscriptions: MyTicketResponse): Share[] {
    const reservedShares = subscriptions.Reservations.map(ticket => ticket.TicketInstance.Share);

    const subscribedShares = subscriptions.Subscriptions.flatMap(subscription =>
      subscription.Tickets.map(ticket => ticket.Share)
    );

    const untaggedShares = subscriptions.UntaggedOwnerships.map(ownership => ownership.Share);
    const shares = [...reservedShares, ...subscribedShares, ...untaggedShares];
    
    return shares;
  }

    private async buildModel(): Promise<void> {
        const orderedShares = this.getOrderedShares();
        const ownedShares = this.ownedShares.length > 0 ? this.ownedShares : orderedShares;
        this.orderedShareCount = orderedShares.length;
        this.ownedShareCount = ownedShares.length;
        this.winChance = this.ticketService.getWinChanceByShares(ownedShares, 0);
        this.circleChance = this.winChance * 100;

        ownedShares.sort((s1: Share, s2: Share) => {
            return s2.Type.Size - s1.Type.Size;
        });

        this.mostValuableTicket = ownedShares[0].Type;
        this.mostValuableTicketString = this.resourceService.getString(`global_share_type_top_prize_currency_${this.mostValuableTicket.Name}`);
        this.mostValuableTicketStringSliced = this.mostValuableTicketString.slice(0, -4);

        const orderPrice = await firstValueFrom(this.priceService.getPriceByOrder(this.orders.orderSubscription));
        this.totalPrice = orderPrice.totalPrice;

    }

    private getOrderedShares(): Share[] {
        if (this.orders.orderSubscription) {
            const allShares: Share[] = this.orders.orderSubscription.TicketPackageInstances.reduce((accumulator: Share[], ticketPackageInstance: TicketPackageInstance) => {
                return [...ticketPackageInstance.Shares, ...accumulator];
            }, [...this.orders.orderSubscription.Shares]);

            return allShares;
        }
        else if (this.orders.orderSmt) {
            const shares: Share[] = [];
            this.orders.orderSmt.Instances.forEach((ins) => {
                shares.push(ins.Share);
            });

            return shares;
        } else {
            return [];
        }
    }

    public setStripHeight(height: number): void {
        this.receiptMiddle.nativeElement.style.paddingTop = `${height}px`;
        this.finalized = true;
    }

    public onReturnClick(): void {
         window.location.href = '/min-side'
    }

    public dispatchEvent(): void {
        if (myEventBus) {
            myEventBus.dispatchEvent(
                new CustomEvent(StorageKeys.BasketTicketsCount, {
                    detail: 0
                })
            );
        }
    }

    public toggleAccordion(): void
    {
        this.accordionIsOpen = !this.accordionIsOpen;
    }

    public toggleGapfundAccordion(): void
    {
        this.accordionGapfundIsOpen = !this.accordionGapfundIsOpen;
    }

    public goToMyPage(): void
    {
        window.location.href = '/min-side'
    }

  public ngOnDestroy(): void {
    this.getPlayerNameSubscription?.unsubscribe();
    this.getPlayerSharesSubscription?.unsubscribe();
    sessionStorage.removeItem(StorageKeys.GapfundDeclined);
  }
}
